import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Chart, Series, CommonSeriesSettings, ArgumentAxis, ValueAxis, Legend, Export, Tooltip, Title, Label } from 'devextreme-react/chart';
import {
  DataGrid,
  Column,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Scrolling,
  Paging,
} from "devextreme-react/data-grid";
import { Close as CloseIcon } from "@mui/icons-material";
import { ChartFilterTagBox as TagBox } from './ChartFilterTagBox';
import { Popover } from 'devextreme-react/popover';
import { OidcIdentityContext } from '@rsmus/react-auth';
import { HQAssignmentDTO, TaxPortalServiceClient } from '../../../api-client-nswag/taxportal-client';
import { createSitesClientApi } from '../../../api-client-nswag/taxportal-client-runtime';
import { convertUTCTimeToLocal } from '../../../util/Utils';
import { IRStatusChart } from './IRCharts';
import { HQISheetName } from '../../../constants';
import { handlePointClick, STATUS_COLOR_MAP } from '../FSServices';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import { SnackbarMessage } from '../../../hooks/useSnackbar.types';
import useSnackbar from '../../../hooks/useSnackbar';


interface Props {
  siteId: number;
}

type ChartRecord = {
  entityName: string;
  [status: string]: string | number;
};

function buildRows(response: HQAssignmentDTO | HQAssignmentDTO[] | null | undefined): HQAssignmentDTO[] {
  if (!response) return [];
  return Array.isArray(response) ? response : [response];
}

function getStatusValue(item: any) {
  return String(item?.status || item.fileStatus || '').trim() || '';
}

function getEntityValue(item: any) {
  return String(item?.entity || item?.entityName || '').trim() || '';
}

function getProjectValue(item: any) {
  return String(item?.projectName || item?.project || item?.projectId || '').trim() || '';
}

function getPhaseValue(item: any) {
  return String(item?.phaseName || item?.engagement || item?.phaseId || '').trim() || '';
}

function getStatusFilterValues(rawData: any[]): string[] {
  return Array.from(new Set(rawData.map((item: any) => getStatusValue(item)).filter(Boolean))).sort();
}

function getEntityFilterValues(rawData: any[]): string[] {
  return Array.from(new Set(rawData.map((item: any) => getEntityValue(item)).filter(Boolean))).sort();
}

function getStatusData(rawData: any[]): ChartRecord[] {
  const grouped: Record<string, Record<string, number>> = {};
  rawData.forEach((item: any) => {
    const entityName = getEntityValue(item);
    const status = getStatusValue(item);
    if (!grouped[entityName]) {
      grouped[entityName] = {};
    }
    grouped[entityName][status] = (grouped[entityName][status] || 0) + 1;
  });

  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .map((entityName) => ({
      entityName,
      ...grouped[entityName],
    }));
}

function customTooltip(arg: { argument: string; valueText: string; seriesName?: string }) {
  return { text: `Entity: ${arg.argument}\nStatus: ${arg.seriesName || ''}\nCount: ${arg.valueText}` };
}

function customizeStatusLabel(pointInfo: { value?: number; valueText: string }) {
  return Number(pointInfo.value || 0) > 0 ? pointInfo.valueText : '';
}

const excludedStatuses = ['Not Applicable'];

function ProjectStatusBarChart(props: {
  rawProjectStatusData: any[];
  siteId: number;
  onRefresh: (type: string) => void;
  onShareClick: (filteredData: any[]) => void;
}) {
  const { user } = useContext(OidcIdentityContext);
  const rawData = props.rawProjectStatusData.filter((item: any) => {
    const status = getStatusValue(item);
    return status !== '' && !excludedStatuses.includes(status);
  });
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState('');
  const [activeSeries, setActiveSeries] = useState<string[]>([]);

  const statusOptions = getStatusFilterValues(rawData);
  const legendFilteredData = activeSeries.length === 0
    ? rawData
    : rawData.filter((item: any) => activeSeries.includes(getStatusValue(item)));
  const entityOptions = getEntityFilterValues(legendFilteredData);
  const filterByEntity = (data: any[]) => entityFilter.length === 0
    ? data
    : data.filter((item: any) => entityFilter.includes(getEntityValue(item)));
  const filteredData = filterByEntity(legendFilteredData);

  // Series are derived from all statuses so legend items remain available for toggling.
  const allStatusChartData = getStatusData(filterByEntity(rawData));
  const statusSeries = statusOptions.filter((status) =>
    allStatusChartData.some((row) => Number(row[status] || 0) > 0)
  );
  const chartData = getStatusData(filteredData);

  useEffect(() => {
    setEntityFilter((curr) => {
      const next = curr.filter((entity) => entityOptions.includes(entity));
      return next.length === curr.length ? curr : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityOptions.join('|')]);

  useEffect(() => {
    if (props.siteId === null || props.siteId === undefined) {
      setLastRefreshTime('');
      return;
    }

    const fetchItemTrack = async () => {
      const itemTrack = await createSitesClientApi.taxportalDbData_GetItemSyncTrackByType(props.siteId, 'SYNC_PS', user?.profile.preferred_username as string);
      const lastModified = (itemTrack as any)?.data?.lastModified ?? (itemTrack as any)?.lastModified;
      const localTime = lastModified?.trim() ? convertUTCTimeToLocal(lastModified): '';
      setLastRefreshTime(localTime);
    };

    fetchItemTrack();
  }, [props.rawProjectStatusData, props.siteId, user?.profile.preferred_username]);

  const handleLegendClick = (event: any) => {
    event.cancel = true;
    const legendKey = event?.target?.name;
    if (!legendKey) return;
    setActiveSeries((curr) =>
      curr.includes(legendKey)
        ? curr.filter((name) => name !== legendKey)
        : [...curr, legendKey]
    );
  };

  return (
    <div>
      <div className="chart-header">
        <div className="chart-title-section1">
          <div className="chart-title-row1">
            <h3 className="chart-title1">Project Status - Status Overview</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                onClick={() => props.onShareClick(filteredData)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 4,
                  border: '1px solid #d0d0d0',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                }}
                title="Data Points"
              >
                <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
              </div>
              <div
                id="ps-status-filter-icon-trigger"
                onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                className={`fs-chart-filter-trigger${entityFilter.length > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                title="Filters"
              >
                <i className="dx-icon-filter" style={{ fontSize: 16, color: '#555' }} aria-hidden="true" />
                {entityFilter.length > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      backgroundColor: '#1976d2',
                      color: '#fff',
                      fontSize: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 3px',
                    }}
                  >
                    1
                  </span>
                )}
              </div>
              <div
                onClick={() => props.onRefresh(HQISheetName.PROJECT_STATUS)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 4,
                  border: '1px solid #d0d0d0',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                }}
                title="Refresh"
              >
                <i className="dx-icon-refresh" style={{ fontSize: 16, color: '#555' }} aria-hidden="true" />
              </div>
            </div>
          </div>
          <span className="chart-refresh-time1">Last Refreshed: {lastRefreshTime}</span>
        </div>
      </div>

      <Popover
        target="#ps-status-filter-icon-trigger"
        visible={isFilterPopoverOpen}
        onHiding={() => setIsFilterPopoverOpen(false)}
        hideOnOutsideClick={true}
        position="bottom"
        width={280}
      >
        <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
            <TagBox
              dataSource={entityOptions}
              value={entityFilter}
              onValueChanged={(e) => setEntityFilter(e.value ?? [])}
              placeholder="All entities"
              showSelectionControls={true}
              applyValueMode="instantly"
              searchEnabled={true}
              width="100%"
            />
          </div>

          {entityFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setEntityFilter([])}
              style={{
                fontSize: 12,
                alignSelf: 'flex-start',
                background: 'none',
                border: 'none',
                color: '#1976d2',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Clear all filters
            </button>
          )}
        </div>
      </Popover>

      {chartData.length > 0 ? (
        <Chart
          id="project-status-bar-chart"
          dataSource={chartData}
          rotated={true}
          onLegendClick={handleLegendClick}
          onPointClick={(event) => handlePointClick(
            event,
            props.siteId,
            HQISheetName.PROJECT_STATUS,
            ({ filterValue, entityName }) => [
              { columnName: 'Status', filterValue: String(filterValue) },
              { columnName: 'Entity Name', filterValue: entityName },
            ],
            (eventArg: any) => ({
              filterValue: String(eventArg?.target?.seriesName || ''),
              entityName: String(eventArg?.target?.argument || ''),
            })
          )}
        >
          <CommonSeriesSettings argumentField="entityName" type="stackedbar">
            <Label visible={true} backgroundColor="none" customizeText={customizeStatusLabel} />
          </CommonSeriesSettings>
          {statusSeries.map((status) => (
            <Series
              key={status}
              valueField={status}
              name={status}
              color={STATUS_COLOR_MAP[status] || '#888b8d'}
              visible={activeSeries.length === 0 || activeSeries.includes(status)}
            />
          ))}
          <ValueAxis>
            <Title text="Count" />
          </ValueAxis>
          <ArgumentAxis>
            <Title text="Entity Name" />
          </ArgumentAxis>
          <Legend visible={true} />
          <Export enabled={false} />
          <Tooltip enabled={true} customizeTooltip={customTooltip} />
        </Chart>
      ) : (
        <div className="no-data-message">No data available</div>
      )}
    </div>
  );
}

export default function SiteProjectStatusWidget({ siteId }: Props) {
  const { user } = useContext(OidcIdentityContext);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [rawIRData, setRawIRData] = useState<HQAssignmentDTO[]>([]);
  const [rawPSData, setRawPSData] = useState<HQAssignmentDTO[]>([]);
  const [isShareViewOpen, setIsShareViewOpen] = useState(false);
  const [selectedProjectStatusData, setSelectedProjectStatusData] = useState<any[]>([]);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchDashboardData = async () => {

      // Only skip when siteId is null or undefined.
      // siteId = 0 should be passed to the API to handle all sites.
      if (siteId === null || siteId === undefined) {
        setRawIRData([]);
        setRawPSData([]);
        return;
      }

      setIsLoading(true);

      try {

        const [irResponse, psResponse] = await Promise.allSettled([
          createSitesClientApi.dashboard_GetISheetItemsByType(
            siteId,
            HQISheetName.INFORMATION_REQUEST,
            user?.profile.preferred_username as string
          ),
          createSitesClientApi.dashboard_GetISheetItemsByType(
            siteId,
            HQISheetName.PROJECT_STATUS,
            user?.profile.preferred_username as string
          ),
        ]);

        setRawIRData(
          irResponse.status === 'fulfilled'
            ? buildRows(irResponse.value)
            : []
        );

        setRawPSData(
          psResponse.status === 'fulfilled'
            ? buildRows(psResponse.value)
            : []
        );
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        setRawIRData([]);
        setRawPSData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [siteId, refreshToken]);

  const refreshDashboard = async (iSheetName: string) => {
    if (siteId === null || siteId === undefined) return;

    try {
      const syncResponse = await createSitesClientApi.syncisheetitems_ISheetName(
        siteId,
        iSheetName,
        user?.profile?.preferred_username as string,
      );

      const syncStatus = String(syncResponse?.status || '').toUpperCase();
      if (syncStatus && syncStatus !== 'SUCCESS') {
        throw new Error(syncResponse?.message || 'Sync request failed');
      }

      setRefreshToken((currentValue) => currentValue + 1);
      showSnackbar({ message: SnackbarMessage.SYNC_CUSTOM_VISUALIZATION_SUCCESS, severity: "info" });

    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_TO_SYNC_PROJECT_STATUS, severity: "error" });
      console.error('Failed to sync Project Status iSheet:', error);
    }
  };

  const projectStatusRows = selectedProjectStatusData.map((item: any) => ({
    status: getStatusValue(item),
    entityName: getEntityValue(item),
    projectName: getProjectValue(item),
    phaseName: getPhaseValue(item),
    siteName: String(item?.hqSiteName || item?.site || '').trim() || '',
  }));

  return (
    <div className="app-container">
      {isLoading ? (
        <div style={{ height: 'calc(100vh - 280px)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <CircularProgress />
          </Box>
        </div>
      ) : (
        <div className="chart-row">
          <div className="chart-container">
            <IRStatusChart
              rawIRData={rawIRData}
              handleRefresh={() => refreshDashboard(HQISheetName.INFORMATION_REQUEST)}
              siteId={siteId}
            />
          </div>
          <div className="chart-container">
            <ProjectStatusBarChart
              rawProjectStatusData={rawPSData}
              siteId={siteId}
              onRefresh={() => refreshDashboard(HQISheetName.PROJECT_STATUS)}
              onShareClick={(filteredData) => {
                setSelectedProjectStatusData(filteredData);
                setIsShareViewOpen(true);
              }}
            />
          </div>
        </div>
      )}

      <Dialog open={isShareViewOpen} onClose={() => setIsShareViewOpen(false)} maxWidth="lg" fullWidth>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6">
            Share View
          </Typography>

          <IconButton
            aria-label="close"
            onClick={() => setIsShareViewOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          <DataGrid
            dataSource={projectStatusRows}
            height={450}
            showBorders
            rowAlternationEnabled
            columnAutoWidth
            wordWrapEnabled
            hoverStateEnabled
            noDataText="No data available"
          >
            <LoadPanel enabled={false} />
            <FilterRow visible />
            <HeaderFilter visible />
            <Paging
              enabled
              defaultPageSize={20}
            />
            <Scrolling mode="standard" />
            <Column
              dataField="status"
              caption="Status"
            />
            <Column
              dataField="entityName"
              caption="Entity"
            />
            <Column
              dataField="projectName"
              caption="Project"
            />
            <Column
              dataField="phaseName"
              caption="Phase"
            />
            <Column
              dataField="siteName"
              caption="Site"
            />
          </DataGrid>
        </DialogContent>
      </Dialog>
    </div>
  );
}